import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Lock, Globe } from "lucide-react";
import { AURORA_SUPPORT_EMAIL, AURORA_SUPPORT_CONTACT_PATH } from "../../lib/contact";

const columns = [
  {
    title: "Index",
    links: [
      { label: "Mission", to: "/mission" },
      { label: "Careers", to: "/careers" },
      { label: "Newsroom", to: "/newsroom" },
      { label: AURORA_SUPPORT_EMAIL, to: AURORA_SUPPORT_CONTACT_PATH },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Safe Dating Tips", to: "/safe-dating-tips" },
      { label: "Trust & Safety", to: "/trust-safety" },
      { label: "FAQ", to: "/faq" },
      { label: "How We Connect Daters", to: "/how-we-connect" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Security", to: "/security" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "Cookie Policy", to: "/cookie-policy" },
      { label: "Your Privacy Choices", to: "/privacy-choices" },
    ],
  },
];

const bottomLinks = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Use", to: "/terms" },
  { label: "Security", to: "/security" },
  { label: "Legal", to: "/legal" },
  { label: "Site Map", to: "/site-map" },
];

function FooterColumn({ column, expanded, onToggle }) {
  return (
    <div>
      {/* Mobile — accordion trigger */}
      <button
        onClick={onToggle}
        className="md:hidden w-full flex items-center justify-between py-4 border-b border-white/[0.06]"
        data-testid={`footer-accordion-${column.title.toLowerCase()}`}
      >
        <span
          className="text-white/60 text-[11px] uppercase tracking-[0.2em]"
          style={{ fontFamily: "'JetBrains Mono', monospace" }}
        >
          {column.title}
        </span>
        <ChevronDown
          size={14}
          className={`text-white/50 transition-transform duration-300 ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Desktop title */}
      <p
        className="hidden md:block text-white/60 text-[11px] uppercase tracking-[0.2em] mb-5"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
      >
        {column.title}
      </p>

      {/* Links */}
      <ul
        className={`md:block ${expanded ? "block" : "hidden"} space-y-3 pt-4 pb-5 md:pt-0 md:pb-0`}
      >
        {column.links.map((link) => (
          <li key={link.label}>
            {link.href ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-[#FF3B6B] text-sm transition-colors"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                data-testid={`footer-link-${link.label.replace(/\W/g, "-").toLowerCase()}`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                to={link.to}
                className="text-white/80 hover:text-[#FF3B6B] text-sm transition-colors"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                data-testid={`footer-link-${link.label.replace(/\W/g, "-").toLowerCase()}`}
              >
                {link.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function AuroraFooter() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <footer
      data-testid="aurora-public-footer"
      className="relative bg-[#050506] border-t border-white/[0.06] mt-24"
    >
      {/* subtle top glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] max-w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF3B6B]/40 to-transparent" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-8">
        {/* Brand row */}
        <div className="mb-12 md:mb-16 flex items-center gap-3">
          <img
            src="/aurora-logo.png"
            alt="Aurora"
            className="w-9 h-9 rounded-xl object-cover ring-1 ring-white/10"
          />
          <div>
            <p
              style={{ fontFamily: "'Bodoni Moda', serif" }}
              className="text-white text-xl tracking-tight"
            >
              Aurora
            </p>
            <p
              className="text-white/40 text-[10px] uppercase tracking-[0.25em]"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              The New Beginning
            </p>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-16">
          {columns.map((col, idx) => (
            <FooterColumn
              key={col.title}
              column={col}
              expanded={openIdx === idx}
              onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
            />
          ))}
        </div>

        {/* Divider */}
        <div className="mt-12 md:mt-20 h-[1px] bg-white/[0.06]" />

        {/* Legal bar */}
        <div className="pt-8 space-y-5">
          {/* Trust + region */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div
              data-testid="footer-trust-badge"
              className="inline-flex items-center gap-2 rounded-full px-3.5 h-8 bg-white/[0.04] border border-white/10"
            >
              <Lock size={12} className="text-[#00E676]" />
              <span
                className="text-white/80 text-[11px] font-medium tracking-wide"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                256-Bit Encrypted &amp; Verified Safe
              </span>
            </div>

            <button
              data-testid="footer-region-selector"
              className="inline-flex items-center gap-2 rounded-full px-3.5 h-8 bg-white/[0.04] border border-white/10 hover:border-white/20 transition-colors"
            >
              <Globe size={12} className="text-white/60" />
              <span
                className="text-white/70 text-[11px] font-medium"
                style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
              >
                United States / English
              </span>
              <ChevronDown size={10} className="text-white/50" />
            </button>
          </div>

          {/* Copyright */}
          <p
            className="text-white/50 text-[11px]"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            data-testid="footer-copyright"
          >
            Copyright © 2026 Aurora Inc. All rights reserved.
          </p>

          {/* Inline legal links */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {bottomLinks.map((l, idx) => (
              <span key={l.label} className="flex items-center gap-2">
                <Link
                  to={l.to}
                  data-testid={`footer-bottom-link-${l.label.replace(/\W/g, "-").toLowerCase()}`}
                  className="text-white/60 hover:text-white text-[11px] transition-colors"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {l.label}
                </Link>
                {idx < bottomLinks.length - 1 && (
                  <span className="text-white/20 text-[11px]">|</span>
                )}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
